import React, { useState, useEffect } from 'react';
import { apiService } from '../../services/api';
import ImageUpload from './ImageUpload';

const HomepageEdit = () => {
  const [homepageData, setHomepageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [formData, setFormData] = useState({
    heroTitle: '',
    heroSubtitle: '',
    heroDescription: '',
    heroBackgroundImage: '',
    quickStats: [
      { number: '', label: '' },
      { number: '', label: '' },
      { number: '', label: '' },
      { number: '', label: '' }
    ],
    featuredPrograms: [],
    newsUpdates: [
      { date: '', title: '', description: '', link: '' },
      { date: '', title: '', description: '', link: '' }
    ],
    whyChooseTitle: '',
    whyChooseDescription: '',
    whyChooseFeatures: [],
    whyChooseImage: '',
    importantLinks: [],
    ctaTitle: '',
    ctaDescription: ''
  });

  useEffect(() => {
    fetchHomepageData();
  }, []);

  const fetchHomepageData = async () => {
    try {
      setLoading(true);
      const response = await apiService.getHomepage();
      const data = response.data;
      
      setHomepageData(data);
      setFormData({
        heroTitle: data.heroTitle || '',
        heroSubtitle: data.heroSubtitle || '',
        heroDescription: data.heroDescription || '',
        heroBackgroundImage: data.heroBackgroundImage || '',
        quickStats: data.quickStats || formData.quickStats,
        featuredPrograms: data.featuredPrograms || [],
        newsUpdates: data.newsUpdates || formData.newsUpdates,
        whyChooseTitle: data.whyChooseTitle || '',
        whyChooseDescription: data.whyChooseDescription || '',
        whyChooseFeatures: (data.whyChooseFeatures || []).map(feature => 
          typeof feature === 'string' 
            ? { heading: feature, description: '', icon: '' } 
            : feature
        ),
        whyChooseImage: data.whyChooseImage || '',
        importantLinks: data.importantLinks || [],
        ctaTitle: data.ctaTitle || '',
        ctaDescription: data.ctaDescription || ''
      });
    } catch (error) {
      setError('Failed to load homepage data');
      console.error('Error fetching homepage:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleArrayChange = (field, index, key, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].map((item, i) => 
        i === index ? { ...item, [key]: value } : item
      )
    }));
  };

  const addFeaturedProgram = () => {
    setFormData(prev => ({
      ...prev,
      featuredPrograms: [...prev.featuredPrograms, { title: '', description: '', icon: '', link: '' }]
    }));
  };

  const removeFeaturedProgram = (index) => {
    setFormData(prev => ({
      ...prev,
      featuredPrograms: prev.featuredPrograms.filter((_, i) => i !== index)
    }));
  };

  const addWhyChooseFeature = () => {
    setFormData(prev => ({
      ...prev,
      whyChooseFeatures: [...prev.whyChooseFeatures, '']
    }));
  };

  const removeWhyChooseFeature = (index) => {
    setFormData(prev => ({
      ...prev,
      whyChooseFeatures: prev.whyChooseFeatures.filter((_, i) => i !== index)
    }));
  };

  const addImportantLink = () => {
    setFormData(prev => ({
      ...prev,
      importantLinks: [...prev.importantLinks, { icon: '', title: '', description: '', link: '', external: false }]
    }));
  };

  const removeImportantLink = (index) => {
    setFormData(prev => ({
      ...prev,
      importantLinks: prev.importantLinks.filter((_, i) => i !== index)
    }));
  };

  const handleSimpleArrayChange = (field, index, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].map((item, i) => i === index ? value : item)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      if (homepageData?.id) {
        await apiService.updateHomepage(homepageData.id, formData);
        setSuccess('Homepage updated successfully!');
      } else {
        await apiService.createHomepage(formData);
        setSuccess('Homepage created successfully!');
      }
      
      // Refresh data
      await fetchHomepageData();
    } catch (error) {
      setError('Failed to save homepage data');
      console.error('Error saving homepage:', error);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg text-slate">
          <i className="fas fa-spinner fa-spin mr-2 text-crimson"></i>
          Loading homepage data...
        </div>
      </div>
    );
  }

  return (
    <div className="font-sans">
      <div className="mb-8">
        <h1 className="text-3xl font-sans font-bold text-slate mb-2">
          Homepage Management
        </h1>
        <p className="text-gray-600">
          Update all homepage content including hero section, stats, programs, and more.
        </p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
          <i className="fas fa-exclamation-circle mr-2"></i>
          {error}
        </div>
      )}

      {success && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg mb-6">
          <i className="fas fa-check-circle mr-2"></i>
          {success}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Hero Section */}
        <div className="bg-white rounded-lg border border-border_gray shadow-sm p-6">
          <h2 className="text-xl font-sans font-semibold text-slate mb-4">Hero Section</h2>
          
          <div className="grid grid-cols-1 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate mb-2">
                Hero Title
              </label>
              <input
                type="text"
                value={formData.heroTitle}
                onChange={(e) => handleInputChange('heroTitle', e.target.value)}
                className="w-full px-4 py-3 border border-border_gray rounded-lg focus:outline-none focus:ring-2 focus:ring-crimson"
                placeholder="Main title for homepage"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate mb-2">
                Hero Subtitle
              </label>
              <input
                type="text"
                value={formData.heroSubtitle}
                onChange={(e) => handleInputChange('heroSubtitle', e.target.value)}
                className="w-full px-4 py-3 border border-border_gray rounded-lg focus:outline-none focus:ring-2 focus:ring-crimson"
                placeholder="Subtitle for homepage"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate mb-2">
                Hero Description
              </label>
              <textarea
                value={formData.heroDescription}
                onChange={(e) => handleInputChange('heroDescription', e.target.value)}
                className="w-full px-4 py-3 border border-border_gray rounded-lg focus:outline-none focus:ring-2 focus:ring-crimson"
                rows={3}
                placeholder="Hero section description"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate mb-2">
                Hero Background Image
              </label>
              <ImageUpload
                value={formData.heroBackgroundImage}
                onChange={(value) => handleInputChange('heroBackgroundImage', value)}
                placeholder="Background image filename"
              />
            </div>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="bg-white rounded-lg border border-border_gray shadow-sm p-6">
          <h2 className="text-xl font-sans font-semibold text-slate mb-4">Quick Stats</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {formData.quickStats.map((stat, index) => (
              <div key={index} className="border border-gray-200 rounded-lg p-4">
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-xs font-medium text-slate mb-1">
                      Number
                    </label>
                    <input
                      type="text"
                      value={stat.number}
                      onChange={(e) => handleArrayChange('quickStats', index, 'number', e.target.value)}
                      className="w-full px-3 py-2 border border-border_gray rounded text-sm"
                      placeholder="50+"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate mb-1">
                      Label
                    </label>
                    <input
                      type="text"
                      value={stat.label}
                      onChange={(e) => handleArrayChange('quickStats', index, 'label', e.target.value)}
                      className="w-full px-3 py-2 border border-border_gray rounded text-sm"
                      placeholder="Programs"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Featured Programs */}
        <div className="bg-white rounded-lg border border-border_gray shadow-sm p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-sans font-semibold text-slate">Featured Programs</h2>
            <button
              type="button"
              onClick={addFeaturedProgram}
              className="px-4 py-2 bg-crimson text-white rounded-lg hover:bg-crimson/90 flex items-center gap-2 text-sm"
            >
              <i className="fas fa-plus"></i>
              Add Program
            </button>
          </div>
          
          <div className="space-y-4">
            {formData.featuredPrograms.length === 0 && (
              <div className="text-center py-8 text-gray-500">
                <i className="fas fa-info-circle mb-2 text-2xl"></i>
                <p>No featured programs added yet. Click "Add Program" to get started.</p>
              </div>
            )}
            {formData.featuredPrograms.map((program, index) => (
              <div key={index} className="border border-gray-200 rounded-lg p-4 relative">
                <button
                  type="button"
                  onClick={() => removeFeaturedProgram(index)}
                  className="absolute top-2 right-2 text-red-500 hover:text-red-700"
                  title="Remove this program"
                >
                  <i className="fas fa-times-circle"></i>
                </button>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-slate mb-1">
                      Title
                    </label>
                    <input
                      type="text"
                      value={program.title}
                      onChange={(e) => handleArrayChange('featuredPrograms', index, 'title', e.target.value)}
                      className="w-full px-3 py-2 border border-border_gray rounded text-sm"
                      placeholder="Program name"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate mb-1">
                      Icon (FontAwesome class)
                    </label>
                    <input
                      type="text"
                      value={program.icon}
                      onChange={(e) => handleArrayChange('featuredPrograms', index, 'icon', e.target.value)}
                      className="w-full px-3 py-2 border border-border_gray rounded text-sm"
                      placeholder="fas fa-laptop-code"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-xs font-medium text-slate mb-1">
                      Description
                    </label>
                    <textarea
                      value={program.description}
                      onChange={(e) => handleArrayChange('featuredPrograms', index, 'description', e.target.value)}
                      className="w-full px-3 py-2 border border-border_gray rounded text-sm"
                      rows={2}
                      placeholder="Program description"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate mb-1">
                      Link
                    </label>
                    <input
                      type="text"
                      value={program.link}
                      onChange={(e) => handleArrayChange('featuredPrograms', index, 'link', e.target.value)}
                      className="w-full px-3 py-2 border border-border_gray rounded text-sm"
                      placeholder="/academics"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Why Choose Section */}
        <div className="bg-white rounded-lg border border-border_gray shadow-sm p-6">
          <h2 className="text-xl font-sans font-semibold text-slate mb-4">Why Choose Section</h2>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate mb-2">
                Section Title
              </label>
              <input
                type="text"
                value={formData.whyChooseTitle}
                onChange={(e) => handleInputChange('whyChooseTitle', e.target.value)}
                className="w-full px-4 py-3 border border-border_gray rounded-lg focus:outline-none focus:ring-2 focus:ring-crimson"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate mb-2">
                Section Description
              </label>
              <textarea
                value={formData.whyChooseDescription}
                onChange={(e) => handleInputChange('whyChooseDescription', e.target.value)}
                className="w-full px-4 py-3 border border-border_gray rounded-lg focus:outline-none focus:ring-2 focus:ring-crimson"
                rows={3}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate mb-2">
                Section Image
              </label>
              <ImageUpload
                value={formData.whyChooseImage}
                onChange={(value) => handleInputChange('whyChooseImage', value)}
                placeholder="Image filename for Why Choose section"
              />
              <p className="text-xs text-gray-500 mt-1">This image will be displayed on the right side of the Why Choose section</p>
            </div>

            <div>
              <div className="flex justify-between items-center mb-2">
                <label className="block text-sm font-medium text-slate">
                  Features
                </label>
                <button
                  type="button"
                  onClick={addWhyChooseFeature}
                  className="px-3 py-1 bg-crimson text-white rounded hover:bg-crimson/90 flex items-center gap-1 text-xs"
                >
                  <i className="fas fa-plus"></i>
                  Add Feature
                </button>
              </div>
              {formData.whyChooseFeatures.length === 0 && (
                <div className="text-center py-6 text-gray-500 border border-dashed border-gray-300 rounded mb-2">
                  <i className="fas fa-info-circle mb-1"></i>
                  <p className="text-sm">No features added yet. Click "Add Feature" to get started.</p>
                </div>
              )}
              {formData.whyChooseFeatures.map((feature, index) => (
                <div key={index} className="border border-gray-200 rounded-lg p-4 mb-3 relative">
                  <button
                    type="button"
                    onClick={() => removeWhyChooseFeature(index)}
                    className="absolute top-2 right-2 text-red-500 hover:text-red-700"
                    title="Remove this feature"
                  >
                    <i className="fas fa-times-circle"></i>
                  </button>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-slate mb-1">
                        Icon (FontAwesome class)
                      </label>
                      <input
                        type="text"
                        value={feature.icon || ''}
                        onChange={(e) => handleArrayChange('whyChooseFeatures', index, 'icon', e.target.value)}
                        className="w-full px-3 py-2 border border-border_gray rounded text-sm"
                        placeholder="fas fa-check-circle"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-xs font-medium text-slate mb-1">
                        Heading
                      </label>
                      <input
                        type="text"
                        value={feature.heading || ''}
                        onChange={(e) => handleArrayChange('whyChooseFeatures', index, 'heading', e.target.value)}
                        className="w-full px-3 py-2 border border-border_gray rounded text-sm"
                        placeholder="Feature heading"
                      />
                    </div>
                    <div className="md:col-span-3">
                      <label className="block text-xs font-medium text-slate mb-1">
                        Description
                      </label>
                      <textarea
                        value={feature.description || ''}
                        onChange={(e) => handleArrayChange('whyChooseFeatures', index, 'description', e.target.value)}
                        className="w-full px-3 py-2 border border-border_gray rounded text-sm"
                        rows={2}
                        placeholder="Feature description"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Important Links */}
        <div className="bg-white rounded-lg border border-border_gray shadow-sm p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-sans font-semibold text-slate">Important Links</h2>
            <button
              type="button"
              onClick={addImportantLink}
              className="px-4 py-2 bg-crimson text-white rounded-lg hover:bg-crimson/90 flex items-center gap-2 text-sm"
            >
              <i className="fas fa-plus"></i>
              Add Link
            </button>
          </div>
          
          <div className="space-y-4">
            {formData.importantLinks.length === 0 && (
              <div className="text-center py-8 text-gray-500">
                <i className="fas fa-info-circle mb-2 text-2xl"></i>
                <p>No important links added yet. Click "Add Link" to get started.</p>
              </div>
            )}
            {formData.importantLinks.map((link, index) => (
              <div key={index} className="border border-gray-200 rounded-lg p-4 relative">
                <button
                  type="button"
                  onClick={() => removeImportantLink(index)}
                  className="absolute top-2 right-2 text-red-500 hover:text-red-700"
                  title="Remove this link"
                >
                  <i className="fas fa-times-circle"></i>
                </button>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-slate mb-1">
                      Icon (FontAwesome class)
                    </label>
                    <input
                      type="text"
                      value={link.icon}
                      onChange={(e) => handleArrayChange('importantLinks', index, 'icon', e.target.value)}
                      className="w-full px-3 py-2 border border-border_gray rounded text-sm"
                      placeholder="fas fa-link"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate mb-1">
                      Title
                    </label>
                    <input
                      type="text"
                      value={link.title}
                      onChange={(e) => handleArrayChange('importantLinks', index, 'title', e.target.value)}
                      className="w-full px-3 py-2 border border-border_gray rounded text-sm"
                      placeholder="Link title"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-xs font-medium text-slate mb-1">
                      Description
                    </label>
                    <input
                      type="text"
                      value={link.description}
                      onChange={(e) => handleArrayChange('importantLinks', index, 'description', e.target.value)}
                      className="w-full px-3 py-2 border border-border_gray rounded text-sm"
                      placeholder="Brief description"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate mb-1">
                      Link URL
                    </label>
                    <input
                      type="text"
                      value={link.link}
                      onChange={(e) => handleArrayChange('importantLinks', index, 'link', e.target.value)}
                      className="w-full px-3 py-2 border border-border_gray rounded text-sm"
                      placeholder="/page or https://example.com"
                    />
                  </div>
                  <div className="flex items-center">
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={link.external || false}
                        onChange={(e) => handleArrayChange('importantLinks', index, 'external', e.target.checked)}
                        className="mr-2"
                      />
                      <span className="text-sm text-slate">External Link (opens in new tab)</span>
                    </label>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Call to Action */}
        <div className="bg-white rounded-lg border border-border_gray shadow-sm p-6">
          <h2 className="text-xl font-sans font-semibold text-slate mb-4">Call to Action Section</h2>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate mb-2">
                CTA Title
              </label>
              <input
                type="text"
                value={formData.ctaTitle}
                onChange={(e) => handleInputChange('ctaTitle', e.target.value)}
                className="w-full px-4 py-3 border border-border_gray rounded-lg focus:outline-none focus:ring-2 focus:ring-crimson"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate mb-2">
                CTA Description
              </label>
              <textarea
                value={formData.ctaDescription}
                onChange={(e) => handleInputChange('ctaDescription', e.target.value)}
                className="w-full px-4 py-3 border border-border_gray rounded-lg focus:outline-none focus:ring-2 focus:ring-crimson"
                rows={3}
              />
            </div>
          </div>
        </div>


        {/* Submit Button */}
        <div className="flex justify-end">
          <button
            type="submit"
            disabled={saving}
            className="px-6 py-3 bg-crimson text-white rounded-lg font-medium hover:bg-crimson/90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {saving ? (
              <>
                <i className="fas fa-spinner fa-spin"></i>
                Saving...
              </>
            ) : (
              <>
                <i className="fas fa-save"></i>
                Save Homepage
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default HomepageEdit;